import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { InvestmentAssetRecord } from '../models/investment-asset.model';
import { InvestmentAssetReadService } from './investment-asset-read.service';

interface LegacyResponse<T> {
  data: T;
  error: boolean;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class InvestmentAssetEntityService {

  constructor(private http: HttpClient, private reads: InvestmentAssetReadService) { }

  get(uid: string): Promise<InvestmentAssetRecord> {
    return this.reads.get(uid).then(record => {
      if (!record) throw new Error();
      return record;
    });
  }

  create(payload: InvestmentAssetRecord): Promise<InvestmentAssetRecord> {
    return this.run(
      this.http.post<LegacyResponse<InvestmentAssetRecord>>(`${environment.apiUrl}/investments`, payload),
      'Compruebe los campos obligatorios marcados en rojo'
    );
  }

  update(uid: string, payload: InvestmentAssetRecord): Promise<InvestmentAssetRecord> {
    return this.run(
      this.http.put<LegacyResponse<InvestmentAssetRecord>>(`${environment.apiUrl}/investments/${uid}/edit`, { ...payload, uid }),
      'Compruebe los campos obligatorios marcados en rojo'
    );
  }

  delete(uid: string): Promise<void> {
    return this.run(
      this.http.delete<LegacyResponse<any>>(`${environment.apiUrl}/investments/${uid}`),
      'No se ha podido eliminar la inversión'
    ).then(() => undefined);
  }

  addAmortisationAccounting(uid: string): Promise<string> {
    const fallback = 'Ha ocurrido un error inesperado al contabilizar la amortización';
    return firstValueFrom(this.http.post<LegacyResponse<string>>(`${environment.apiUrl}/investment/add/amortisation/year`, { investmentUid: uid }))
      .catch((error: any) => {
        throw { error: { message: (error && error.error && typeof error.error.data === 'string' && error.error.data) || fallback } };
      })
      .then(response => {
        const message = response && typeof response.data === 'string' ? response.data : '';
        if (!response || response.error === true) throw { error: { message: message || fallback } };
        return message;
      });
  }

  deleteAmortisationAccounting(uid: string): Promise<void> {
    return this.run<boolean>(
      this.http.post<LegacyResponse<boolean>>(`${environment.apiUrl}/investment/delete/amortisation/year`, { investmentUid: uid }),
      'No se ha eliminado la contabilidad de la amortización'
    ).then(ok => {
      if (ok !== true) throw { error: { message: 'No se ha eliminado la contabilidad de la amortización' } };
    });
  }

  private run<T>(request: any, fallback: string): Promise<T> {
    return firstValueFrom(request)
      .catch((error: any) => {
        const message = error && error.error && typeof error.error.message === 'string' && error.error.message.trim() ? error.error.message : fallback;
        throw { status: error && error.status, error: { message } };
      })
      .then((response: LegacyResponse<T>) => {
        if (!response || response.error === true) {
          const message = response && typeof response.message === 'string' && response.message.trim() ? response.message : fallback;
          throw { error: { message } };
        }
        return response.data;
      });
  }
}
